import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { authService } from '../services/api';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';

// 用於調試的日誌功能
const debugLog = (message, data) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log(`[AuthContext Debug] ${message}`, data);
  }
};

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Get user profile - wrapped in useCallback to stabilize function reference
  const getProfile = useCallback(async () => {
    try {
      setLoading(true);
      const profile = await authService.getProfile();
      
      // console.log('Profile received from API:', profile);
      
      // Extract company name if available, with better fallback handling
      const companyName = profile.company?.name || 
                          profile.companyName || 
                          (profile.company ? '未知公司名稱' : '未指定公司');
      
      // Update currentUser with the new profile data and preserve both company formats
      setCurrentUser(prevUser => {
        const updatedUser = {
          ...prevUser,
          ...profile,
          // Explicitly preserve the company object
          company: profile.company || prevUser.company,
          // Also maintain a flat companyName property for easier access
          companyName: companyName
        };
        
        // console.log('Updated user state:', updatedUser);
        return updatedUser;
      });
      
      return profile;
    } catch (err) {
      console.error('Error in getProfile:', err);
      setError(err.response?.data?.message || 'Failed to get profile');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);  // Empty dependency array to ensure stable reference
  
  // Initialize user from localStorage and fetch profile if needed
  useEffect(() => {
    const initializeUser = async () => {
      // debugLog('初始化用戶數據');
      setLoading(true);
      
      try {
        // Check if user is already logged in
        const user = authService.getCurrentUser();
        
        if (user) {
          // debugLog('找到已保存的用戶數據', { id: user._id, email: user.email });
          // First set the current user with basic data from localStorage
          setCurrentUser(user);
          
          // Then fetch the complete profile to get the company name
          try {
            // debugLog('獲取完整用戶資料');
            await getProfile();
            // debugLog('用戶資料獲取成功');
          } catch (err) {
            console.error('Error fetching profile during initialization:', err);
            debugLog('獲取用戶資料失敗', { error: err.message });
            
            // 雖然獲取資料失敗，但我們已經有了基本數據，所以繼續使用
          }
        } else {
          debugLog('未找到保存的用戶數據，用戶未登入');
        }
      } catch (error) {
        console.error('Error in user initialization:', error);
        debugLog('用戶初始化過程中發生錯誤', { error: error.message });
      } finally {
        setLoading(false);
      }
    };
    
    initializeUser();
  }, [getProfile]);

  // Register a new user
  const register = async (userData) => {
    try {
      setLoading(true);
      setError(null);
      const user = await authService.register(userData);
      setCurrentUser(user);
      navigate('/');
      return user;
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Login user
  const login = async (credentials) => {
    try {
      setLoading(true);
      setError(null);
      debugLog('開始登入流程', { email: credentials.email });
      
      const user = await authService.login(credentials);
      debugLog('登入成功，設置用戶數據');
      setCurrentUser(user);
      
      // After login, immediately get full profile to get company name
      // Wrapped in separate try/catch to prevent login failure if profile fetch fails
      try {
        // debugLog('開始獲取完整用戶資料');
        await getProfile();
        debugLog('用戶資料獲取成功');
      } catch (profileErr) {
        console.error('Error fetching profile after login:', profileErr);
        debugLog('獲取用戶資料失敗，但繼續保持登入狀態', { error: profileErr.message });
      }
      
      debugLog('登入流程完成，重定向到儀表板');
      navigate('/admin');
      return user;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Login failed';
      debugLog('登入失敗', { 
        error: err.message,
        status: err.response?.status,
        serverMessage: err.response?.data?.message 
      });
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Logout user
  const logout = () => {
    authService.logout();
    setCurrentUser(null);
    navigate('/login');
  };

  // Update user profile
  const updateProfile = async (profileData) => {
    try {
      setLoading(true);
      debugLog('開始更新用戶資料', profileData);
      
      const updatedProfile = await authService.updateProfile(profileData);
      debugLog('用戶資料更新成功', updatedProfile);
      
      // Preserve the companyName when updating the profile
      // Use functional update pattern for consistency and to avoid stale closures
      setCurrentUser(prevUser => {
        debugLog('更新當前用戶狀態', { 
          previous: prevUser, 
          updates: updatedProfile,
          profileData
        });
        
        return {
          ...prevUser,
          ...updatedProfile,
          // If username was updated, explicitly use it from the response or the request
          username: updatedProfile.username || profileData.username || prevUser.username,
          // If name was updated, explicitly use it from the response or the request
          name: updatedProfile.name || profileData.name || prevUser.name,
          // If email was updated, explicitly use it from the response or the request
          email: updatedProfile.email || profileData.email || prevUser.email,
          // Keep existing companyName
          companyName: prevUser.companyName
        };
      });
      
      return updatedProfile;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update profile';
      debugLog('更新用戶資料失敗', { 
        error: err.message,
        status: err.response?.status,
        serverMessage: err.response?.data?.message 
      });
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    currentUser,
    loading,
    error,
    register,
    login,
    logout,
    getProfile,
    updateProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
